import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';

export interface CompanyPickerProps {
  /** Selected company id, or null/empty for unset. */
  value: string | null;
  onChange: (companyId: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  /**
   * If true, render even when the tenant only has one company. Default false:
   * the picker hides itself in single-company tenants and the caller should
   * fall back to the only company. (See docs/platform/multi-company-design.md.)
   */
  showWhenSingle?: boolean;
  /** Optional override list (e.g. all tenant companies for admins) — defaults to caller's accessible set. */
  companies?: Array<{ id: string; name: string }>;
  className?: string;
  id?: string;
}

/**
 * Single-select picker over the user's accessible companies. Hides itself in
 * single-company tenants (the canonical "hide-when-single" pattern). Use for
 * S-pattern form fields on multi-company-aware records.
 */
export function CompanyPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  showWhenSingle = false,
  companies,
  className,
  id,
}: CompanyPickerProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const { data, isLoading } = useMyCompanies();
  const options = companies ?? data?.data ?? [];

  if (!showWhenSingle && options.length <= 1) {
    return null;
  }

  const resolvedPlaceholder = placeholder ?? t('common:pickers.selectCompany', 'Select a company');

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Select
        value={value ?? undefined}
        onValueChange={(v) => onChange(v || null)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger id={id} className={cn(error && 'border-destructive')}>
          <SelectValue placeholder={resolvedPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <span className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                {c.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {(errorMessage || helperText) && (
        <p
          className={cn(
            'text-xs',
            error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}
